import React, { useState, useEffect } from "react";
import { Typography } from "antd";
import { useCallHistoryProvider } from "../context/CallHIstoryContext";
import { useTheme } from "../../../ThemeContext";

const { Text } = Typography;

export const CallStatusPanel = () => {
  const { 
    callList, 
    callStatusFilter,
    setCallStatusFilter,
    filterCallsByStatus,
    filterBy,
    setFilterBy,
    filterType,
    setFilterType,
    sortCalls,
    setSelectedMember,
    setSelectedCall,
    filterCallsByMember
  } = useCallHistoryProvider();
  
  const { currentTheme, themeMode } = useTheme();
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    scheduled: 0,
    avgDuration: 0,
    thisMonthCalls: 0,
    thisMonthDuration: 0,
    avgCallTime: 15
  });

  // Calculate statistics - these remain constant regardless of filters
  useEffect(() => {
    const total = callList.length;
    const completed = callList.filter(call => call.call_status === 'completed').length;
    const scheduled = callList.filter(call => call.call_status === 'scheduled').length;
    
    // Calculate call duration analytics
    const completedCalls = callList.filter(call => call.call_status === 'completed');
    const totalDuration = completedCalls.reduce((sum, call) => sum + (call.total_duration || 0), 0);
    const avgDuration = completedCalls.length > 0 ? Math.round(totalDuration / completedCalls.length) : 0;
    
    // Calculate time-based patterns
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Calls made this month
    const thisMonthCalls = completedCalls.filter(call => {
      if (!call.call_start_time) return false;
      const callDate = new Date(call.call_start_time);
      return callDate.getMonth() === currentMonth && callDate.getFullYear() === currentYear;
    });
    
    const thisMonthDuration = thisMonthCalls.reduce((sum, call) => sum + (call.total_duration || 0), 0);

    setStats({
      total,
      completed,
      scheduled,
      avgDuration,
      thisMonthCalls: thisMonthCalls.length,
      thisMonthDuration,
      avgCallTime: 15 // Default to 15:00 as shown in screenshot
    });
  }, [callList]);

  const handleStatusFilter = (status) => {
    setCallStatusFilter(status);
    filterCallsByStatus(status);
  };

  // Filter handlers
  const handleFilterChange = () => {
    if (filterBy === "date") {
      setFilterBy("name");
    } else {
      setFilterBy("date");
    }
    applySorting();
  };

  const handleFilterTypeChange = () => {
    if (filterType === "DESC") {
      setFilterType("ASC");
    } else {
      setFilterType("DESC");
    }
    applySorting();
  };

  const applySorting = () => {
    const sortBy = filterBy === "name" ? "member_name" : "call_start_time";
    sortCalls(sortBy, filterType);
  };

  const handleReset = () => {
    console.log("Resetting all filters and showing all calls...");
    
    // Reset all filter states
    setFilterBy('date');
    setFilterType('DESC');
    setCallStatusFilter('all');
    
    // Clear selected member and selected call
    setSelectedMember('');
    setSelectedCall('');
    
    // Reset member filtering to show all calls
    filterCallsByMember('');
    
    // Apply default sorting
    sortCalls("call_start_time", "DESC");
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  return (
    <div style={{ 
      padding: '24px', 
      height: '85vh', 
      overflowY: 'auto',
      backgroundColor: currentTheme.callhistory.mainBackground 
    }}>
      
      {/* Combined Search Filters & Call Status Section */}
      <div style={{
        backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        border: `1px solid ${currentTheme.callhistory.borderColor}`,
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <Text style={{
          color: currentTheme.callhistory.text,
          fontSize: '18px',
          fontWeight: '600',
          display: 'block',
          marginBottom: '24px'
        }}>
          Search Filters & Call Status
        </Text>
        
        {/* Search Filters Row */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '16px',
          marginBottom: '24px'
        }}>
          {/* Sorted By Filter */}
          <div
            onClick={handleFilterChange}
            style={{
              padding: '16px',
              backgroundColor: currentTheme.callhistory.mainBackground,
              borderRadius: '8px',
              border: `1px solid ${currentTheme.callhistory.borderColor}`,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
          >
            <Text style={{ 
              color: currentTheme.callhistory.textLight, 
              fontSize: '12px', 
              display: 'block',
              marginBottom: '8px'
            }}>
              Sorted By
            </Text>
            <Text style={{ 
              color: currentTheme.callhistory.text, 
              fontSize: '16px', 
              fontWeight: '500',
              textTransform: 'capitalize'
            }}>
              {filterBy}
            </Text>
          </div>
          
          {/* Sort Order Filter */}
          <div
            onClick={handleFilterTypeChange}
            style={{
              padding: '16px',
              backgroundColor: currentTheme.callhistory.mainBackground,
              borderRadius: '8px',
              border: `1px solid ${currentTheme.callhistory.borderColor}`,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
          >
            <Text style={{ 
              color: currentTheme.callhistory.textLight, 
              fontSize: '12px', 
              display: 'block',
              marginBottom: '8px'
            }}>
              Sort Order
            </Text>
            <Text style={{ 
              color: currentTheme.callhistory.text, 
              fontSize: '16px', 
              fontWeight: '500'
            }}>
              {filterType}
            </Text>
          </div>
          
          {/* Reset Button */}
          <div
            onClick={handleReset}
            style={{
              padding: '16px',
              backgroundColor: currentTheme.loginPage.loginButtonColor,
              borderRadius: '8px',
              border: `1px solid ${currentTheme.loginPage.loginButtonColor}`,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
          >
            <Text style={{ 
              color: '#fff', 
              fontSize: '12px', 
              display: 'block',
              marginBottom: '8px'
            }}>
              Action
            </Text>
            <Text style={{ 
              color: '#fff', 
              fontSize: '16px', 
              fontWeight: '500'
            }}>
              Reset
            </Text>
          </div>
        </div>

        {/* Call Status Row */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '16px' 
        }}>
          {/* All Calls */}
          <div
            onClick={() => handleStatusFilter('all')}
            style={{
              padding: '24px',
              backgroundColor: callStatusFilter === 'all' ? currentTheme.loginPage.loginButtonColor : currentTheme.callhistory.mainBackground,
              borderRadius: '12px',
              border: `2px solid ${callStatusFilter === 'all' ? currentTheme.loginPage.loginButtonColor : currentTheme.callhistory.borderColor}`,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
          >
            <Text style={{ 
              color: callStatusFilter === 'all' ? '#fff' : currentTheme.callhistory.text, 
              fontSize: '48px', 
              fontWeight: '300',
              display: 'block',
              lineHeight: '1',
              marginBottom: '12px'
            }}>
              {stats.total}
            </Text>
            <Text style={{ 
              color: callStatusFilter === 'all' ? '#fff' : currentTheme.callhistory.text, 
              fontSize: '16px', 
              fontWeight: '400'
            }}>
              All Calls
            </Text>
          </div>
          
          {/* Completed */}
          <div
            onClick={() => handleStatusFilter('completed')}
            style={{
              padding: '24px',
              backgroundColor: callStatusFilter === 'completed' ? currentTheme.loginPage.loginButtonColor : currentTheme.callhistory.mainBackground,
              borderRadius: '12px',
              border: `2px solid ${callStatusFilter === 'completed' ? currentTheme.loginPage.loginButtonColor : currentTheme.callhistory.borderColor}`,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
          >
            <Text style={{ 
              color: callStatusFilter === 'completed' ? '#fff' : currentTheme.callhistory.text, 
              fontSize: '48px', 
              fontWeight: '300',
              display: 'block',
              lineHeight: '1',
              marginBottom: '12px'
            }}>
              {stats.completed}
            </Text>
            <Text style={{ 
              color: callStatusFilter === 'completed' ? '#fff' : currentTheme.callhistory.text, 
              fontSize: '16px', 
              fontWeight: '400'
            }}>
              Completed
            </Text>
          </div>
          
          {/* Scheduled */}
          <div
            onClick={() => handleStatusFilter('scheduled')}
            style={{
              padding: '24px',
              backgroundColor: callStatusFilter === 'scheduled' ? currentTheme.loginPage.loginButtonColor : currentTheme.callhistory.mainBackground,
              borderRadius: '12px',
              border: `2px solid ${callStatusFilter === 'scheduled' ? currentTheme.loginPage.loginButtonColor : currentTheme.callhistory.borderColor}`,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}
          >
            <Text style={{ 
              color: callStatusFilter === 'scheduled' ? '#fff' : currentTheme.callhistory.text, 
              fontSize: '48px', 
              fontWeight: '300',
              display: 'block',
              lineHeight: '1',
              marginBottom: '12px'
            }}>
              {stats.scheduled}
            </Text>
            <Text style={{ 
              color: callStatusFilter === 'scheduled' ? '#fff' : currentTheme.callhistory.text, 
              fontSize: '16px', 
              fontWeight: '400'
            }}>
              Scheduled
            </Text>
          </div>
        </div>
      </div>

      {/* Call Summary Section */}
      <div style={{
        backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        border: `1px solid ${currentTheme.callhistory.borderColor}`,
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <Text style={{
          color: currentTheme.callhistory.text,
          fontSize: '18px',
          fontWeight: '600',
          display: 'block',
          marginBottom: '24px'
        }}>
          Call Summary
        </Text>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '24px' 
        }}>
          {/* Average Duration */}
          <div>
            <Text style={{ 
              color: currentTheme.callhistory.textLight, 
              fontSize: '14px', 
              display: 'block',
              marginBottom: '8px'
            }}>
              Average Duration
            </Text>
            <Text style={{ 
              color: currentTheme.callhistory.text, 
              fontSize: '24px', 
              fontWeight: '400'
            }}>
              {formatDuration(stats.avgDuration)}
            </Text>
          </div>
          
          {/* Average Call Time */}
          <div>
            <Text style={{ 
              color: currentTheme.callhistory.textLight, 
              fontSize: '14px', 
              display: 'block',
              marginBottom: '8px'
            }}>
              Average Call Time
            </Text>
            <Text style={{ 
              color: currentTheme.callhistory.text, 
              fontSize: '24px', 
              fontWeight: '400'
            }}>
              {stats.avgCallTime}:00
            </Text>
          </div>
        </div>
      </div>

      {/* This Month's Summary Section */}
      <div style={{
        backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        border: `1px solid ${currentTheme.callhistory.borderColor}`,
        borderRadius: '12px',
        padding: '24px'
      }}>
        <Text style={{
          color: currentTheme.callhistory.text,
          fontSize: '18px',
          fontWeight: '600',
          display: 'block',
          marginBottom: '24px'
        }}>
          This month's summary
        </Text>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '24px' 
        }}>
          {/* Average Call Time */}
          <div>
            <Text style={{ 
              color: currentTheme.callhistory.textLight, 
              fontSize: '14px', 
              display: 'block',
              marginBottom: '8px'
            }}>
              Average Call Time
            </Text>
            <Text style={{ 
              color: currentTheme.callhistory.text, 
              fontSize: '24px', 
              fontWeight: '400'
            }}>
              {formatDuration(stats.avgDuration)}
            </Text>
          </div>
          
          {/* Total Successful Time */}
          <div>
            <Text style={{ 
              color: currentTheme.callhistory.textLight, 
              fontSize: '14px', 
              display: 'block',
              marginBottom: '8px'
            }}>
              Total Successful Time
            </Text>
            <Text style={{ 
              color: currentTheme.callhistory.text, 
              fontSize: '24px', 
              fontWeight: '400'
            }}>
              {formatTime(stats.thisMonthDuration)}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}; 